import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  TextField,
  Typography,
} from "@mui/material"
import { Search as SearchIcon } from "@mui/icons-material"
import { useGetChatsQuery } from "../../store/chatAnalyzeApi"
import { ChatLibraryItem } from "./ChatLibraryItem"
import { DropZone } from "./DropZone"
import { SourceFilterChips } from "./sourceFilterChips"
import { UploadDialog } from "./UploadDialog"
import { ChatExportForm } from "./chatExportForm"
import type { ChatAnalyze } from "../interfaces"

interface ChatLibraryPanelProps {
  selectedChatId: string | null
  /** Source-фильтр библиотеки — контролируемый; состояние живёт в ChatAnalyzePage,
   *  чтобы тот же фильтр сужал кандидатов пересечений (дельта 2026-09-22 §1.1). */
  source: string | undefined
  onSourceChange: (source: string | undefined) => void
  onSelect: (chat: ChatAnalyze.SavedChat) => void
}

export function ChatLibraryPanel({
  selectedChatId,
  source,
  onSourceChange,
  onSelect,
}: ChatLibraryPanelProps) {
  const [uploadOpen, setUploadOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [search, setSearch] = useState("")
  // useGetChatsQuery is subscribed here; UploadDialog's useSaveChatMutation
  // invalidates the "SavedChat LIST" tag, causing this query to auto-refetch.
  const {
    data: chats,
    isLoading,
    isError,
    refetch,
  } = useGetChatsQuery(source ? { source } : undefined)

  const filtered = (chats ?? []).filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  )

  const handleDropFiles = (files: File[]) => {
    setPendingFiles(files)
    setUploadOpen(true)
  }

  const handleDialogClose = () => {
    setUploadOpen(false)
    setPendingFiles([])
  }

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Mini drop zone */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <DropZone
          variant="mini"
          onFiles={handleDropFiles}
          onClick={() => setUploadOpen(true)}
        />
      </Box>

      {/* Сбор чата через юзербота (только с правами записи в userbot) */}
      <ChatExportForm onStarted={refetch} />

      {/* Search */}
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Поиск..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Список + фильтр по источнику (§1.1): пустой выбор = без параметра */}
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <SourceFilterChips source={source} onChange={onSourceChange} />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 1 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => refetch()}>
                Повторить
              </Button>
            }
            sx={{ mx: 0.5 }}
          >
            Ошибка загрузки
          </Alert>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ pt: 4, px: 2 }}
          >
            {search
              ? "Ничего не найдено"
              : source
                ? "Нет чатов с анализами выбранных источников"
                : "Библиотека пуста. Добавьте первый чат."}
          </Typography>
        )}
        {!isLoading && !isError && (
          <List dense disablePadding>
            {filtered.map((chat) => (
              <ChatLibraryItem
                key={chat.id}
                chat={chat}
                selected={selectedChatId === chat.id}
                onClick={() => onSelect(chat)}
              />
            ))}
          </List>
        )}
      </Box>

      <UploadDialog
        open={uploadOpen}
        onClose={handleDialogClose}
        initialFiles={pendingFiles.length > 0 ? pendingFiles : undefined}
      />
    </Box>
  )
}
